import { ComponentProps, useMemo } from 'react';

import { MyFavoriteCarouselCard } from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { useNavigate } from 'react-router-dom';
import { useListStoreItems } from 'store/Api';
import { Button, DEFAULT_IMAGE_PLACEHOLDER, Group, Stack, Text, Title, centsToDollar } from 'ui';

import { useCausePageContext } from '../../CauseContext';

const AboutProducts = () => {
  const { cause } = useCausePageContext();
  const { isSmallScreen } = useBreakPoints();
  const navigate = useNavigate();

  const { data: productsData } = useListStoreItems({
    queryOptions: {
      order: {
        field: 'order',
        direction: 'asc',
      },
      join: 'AND',
      searches: {
        type: {
          value: 'StoreItems::Item',
        },
        in_store: {
          value: true,
        },
        live: {
          value: true,
        },
      },
      options: {
        total: true,
      },
    },
    pagination: {
      limit: 5,
    },
    causeId: cause.uuid,
  });

  const carouselItems = useMemo(() => {
    if (!productsData) return [];

    return productsData.store_items.map((product) => {
      return {
        name: product.name,
        image: product.images[0]?.url ?? DEFAULT_IMAGE_PLACEHOLDER,
        onClick: () => navigate(`../shop/items/${product.uuid}`),
        subtitle: (
          <Text fz="xxxs" fw={600} c="mint.7" truncate>
            {centsToDollar(product.price)}
          </Text>
        ),
      } as ComponentProps<typeof MyFavoriteCarouselCard>['items'][number];
    });
  }, [productsData, navigate]);

  if (!productsData?.store_items.length) {
    return null;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2} c="gray.8">
          Store Products
        </Title>

        <Button variant="subtle" onClick={() => navigate(`../shop`)} size="sm">
          View All
        </Button>
      </Group>

      <MyFavoriteCarouselCard
        hideHeader
        items={carouselItems}
        numTotalItems={productsData?.meta.total}
        maxVisibleItems={5}
        onViewAllClick={() => navigate(`../shop`)}
        aspectRatio={3 / 2}
        alwaysShowInCarousel
        slidesToScroll={isSmallScreen ? 2 : 3}
      />
    </Stack>
  );
};

export { AboutProducts };
